export const componentRef = (component, index) => `component:${component?.component_ref || index}`;

export const componentIndexForRef = (ref, components = []) => {
  if (typeof ref !== "string" || !ref.startsWith("component:")) return null;
  const value = ref.slice("component:".length);
  if (/^\d+$/.test(value)) {
    const index = Number(value);
    return index >= 0 && index < components.length ? index : null;
  }
  const stable = components.map(component => String(component?.component_ref || "")).indexOf(value);
  if (stable >= 0) return stable;
  const legacy = components.map(component => String(component?.component_id || "")).filter(id => id === value).length;
  return legacy === 1 ? components.map(component => String(component?.component_id || "")).indexOf(value) : null;
};

const componentIndexes = (flow, components) => new Set(
  (flow?.stages || []).map(stage => stage.input_refs || []).flat()
    .map(ref => componentIndexForRef(ref, components))
    .filter(index => index != null),
);

export const normaliseQptComponentReferences = (flow, components = []) => {
  if (!flow) return flow;
  return {
    ...flow,
    stages: (flow.stages || []).map(stage => ({
      ...stage,
      input_refs: (stage.input_refs || []).map(ref => {
        const index = componentIndexForRef(ref, components);
        return index == null ? ref : componentRef(components[index], index);
      }),
    })),
  };
};

// The editor uses indexes only as a short-lived display convenience. Saving
// always translates them back to component_ref, so deleting/reordering a row
// cannot change the persisted process identity.
export const qptComponentIndexesForEditor = (flow, components = []) => {
  if (!flow) return flow;
  return {
    ...flow,
    stages: (flow.stages || []).map(stage => ({
      ...stage,
      input_refs: (stage.input_refs || []).map(ref => {
        const index = componentIndexForRef(ref, components);
        return index == null ? ref : `component:${index}`;
      }),
    })),
  };
};

export const isEligibleStageResult = stage => {
  const action = String(stage?.action || "").trim();
  return Boolean(stage?.id && action && !/^stage\s*\d+$/i.test(action) && (stage.input_refs || []).length);
};

// A single-stage recipe has only one logical destination. Only apply this
// convenience when the user has made no component assignment at all.
export const normaliseSingleStageComponentAssignments = (flow, components = []) => {
  if (!flow || (flow.stages || []).length !== 1 || !components.length) return flow;
  if (componentIndexes(flow, components).size) return flow;
  const [stage] = flow.stages;
  return {
    ...flow,
    stages: [{
      ...stage,
      input_refs: [...(stage.input_refs || []), ...components.map(componentRef)],
    }],
  };
};

export const unassignedComponentIndexes = (flow, components = []) => {
  const assigned = componentIndexes(flow, components);
  return components.map((_, index) => index).filter(index => !assigned.has(index));
};
